import { Address, AppSettings, PaymentMethodIdEnum, ShippingMethod, ShippingMethodType, TTaxiShippingQuote } from "@core/types";
import dayjs from "dayjs";
import { getApplicableShippingMethod } from "../../utils/getApplicableShippingMethod";
import { toast } from "react-toastify";
import { OrderDataState } from "../orderDataStore/types";

export const TAXI_MASTER_PAYMENT_METHOD = {
  id: "2",
  title: "Безналичная оплата",
  type: PaymentMethodIdEnum.card,
} as const;

export const hasTaxiMasterShippingMethod = (shippingMethods: ShippingMethod[]) =>
  shippingMethods.some((method) => method.methodType === ShippingMethodType.taxiMaster);

/** Любой из флагов быстрой курьерской доставки (TaxiMaster / Wayset) */
export const isFastCourierFeatureEnabled = (config: AppSettings) =>
  !!config.isTaxiMasterEnabled || !!config.isWaysetEnabled;

/** Feat flag из конфига + метод TAXI_MASTER в доступных shipping methods зоны */
export const isTaxiMasterAvailable = ({
  config,
  shippingMethods,
}: {
  config: AppSettings;
  shippingMethods: ShippingMethod[];
}) => {
  return isFastCourierFeatureEnabled(config) && hasTaxiMasterShippingMethod(shippingMethods);
};

export const getAddressRecalcKey = (
  addressId: string,
  address: { latitude?: string; longitude?: string; fullAddress?: string; house?: string; street?: string } | null,
) => {
  if (!addressId || !address) {
    return "";
  }

  return [
    addressId,
    address.latitude ?? "",
    address.longitude ?? "",
    address.fullAddress ?? "",
    address.house ?? "",
    address.street ?? "",
  ].join(":");
};

let activeRequest: { key: string; promise: Promise<TTaxiShippingQuote | null> } | null = null;

export const dedupedCalculateTaxiShipping = (
  address: Address,
  calculate: (params: { customerAddress: Address }) => Promise<TTaxiShippingQuote | null>,
): Promise<TTaxiShippingQuote | null> => {
  const key = getAddressRecalcKey(address.id ?? "", address);

  if (activeRequest?.key === key) return activeRequest.promise;

  const promise = calculate({ customerAddress: address }).finally(() => {
    if (activeRequest?.key === key) activeRequest = null;
  });

  activeRequest = { key, promise };
  return promise;
};

export const isTaxiQuoteExpired = (quote: TTaxiShippingQuote | null | undefined): boolean => {
  if (!quote?.quoteExpiresAt) {
    return false;
  }

  return dayjs(quote.quoteExpiresAt).isBefore(dayjs());
};

type UpdateOrderData = (data: Partial<OrderDataState>) => void;

type ApplyTaxiMasterShippingUpdateParams = {
  isCourierServiceTurned: boolean;
  taxiShippingQuote?: TTaxiShippingQuote | null;
  enabledShippingMethods: ShippingMethod[];
  cartSubtotalValue: number;
  deliveryDate: Date | null;
  updateOrderData: UpdateOrderData;
  showInvalidCostToast?: boolean;
};

export const applyTaxiMasterShippingUpdate = ({
  isCourierServiceTurned,
  taxiShippingQuote,
  enabledShippingMethods,
  cartSubtotalValue,
  deliveryDate,
  updateOrderData,
  showInvalidCostToast = true,
}: ApplyTaxiMasterShippingUpdateParams) => {
  if (isCourierServiceTurned) {
    const taxiShippingCost = taxiShippingQuote?.total;

    if (taxiShippingCost == null || taxiShippingCost === 0) {
      if (showInvalidCostToast) {
        toast.error(
          "Стоимость доставки не рассчитана. Пожалуйста, подождите или переключите метод доставки для расчета",
        );
      }
      return false;
    }

    const taxiMaster = enabledShippingMethods.find((shipping) => shipping.methodType === ShippingMethodType.taxiMaster);

    if (!taxiMaster) {
      return false;
    }

    const today = dayjs().startOf("day").toDate();

    updateOrderData({
      shippingMethod: { ...taxiMaster, cost: taxiShippingCost.toString() },
      taxiQuote: taxiShippingQuote ?? null,
      paymentMethod: TAXI_MASTER_PAYMENT_METHOD,
      deliveryDate: today,
    });

    return true;
  }

  const nonTaxiMethods = enabledShippingMethods.filter((method) => method.methodType !== ShippingMethodType.taxiMaster);
  const method = getApplicableShippingMethod(nonTaxiMethods, cartSubtotalValue);

  if (method) {
    updateOrderData({ shippingMethod: method, taxiQuote: null });
  }

  return true;
};
